/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { weekdayNames } from "../../shared/dates";
import { templateCode, type DayTemplate } from "../types";

type Props = {
  templates: DayTemplate[];
  /** weekday (1–7) → template id */
  assigned: Record<number, number | undefined>;
  onAssign: (weekday: number, templateId: number | null) => void;
};

// Seven day pills showing each day's template; pick a day, then its template.
export const WeekdayAssign = defineComponent<Props>({
  name: "WeekdayAssign",
  props: ["templates", "assigned", "onAssign"],
  setup(props) {
    const { t } = useLocalization();
    const names = weekdayNames("short");
    const day = ref(1);

    return () => {
      const current = props.templates.find((x) => x.id === props.assigned[day.value]);
      return (
        <div class="rounded-2xl border border-base-300 bg-base-100 p-5">
          <div class="mb-1 text-lg font-semibold">{t("workday.rota.assign_title")}</div>
          <p class="mb-4 text-sm text-base-content/60">{t("workday.rota.assign_help")}</p>
          <div class="grid grid-cols-7 gap-2">
            {names.map((n, i) => {
              const wd = i + 1;
              const tpl = props.templates.find((x) => x.id === props.assigned[wd]);
              const on = day.value === wd;
              return (
                <button
                  key={wd}
                  class={[
                    "flex flex-col items-center rounded-xl border py-2 transition",
                    on ? "border-primary bg-primary/10 ring-2 ring-primary/20" : "border-base-300 hover:border-base-content/30",
                  ].join(" ")}
                  onClick={() => (day.value = wd)}
                >
                  <span class="text-sm font-semibold">{n}</span>
                  <span class={["text-[11px]", tpl ? "text-base-content/70" : "text-base-content/30"].join(" ")}>
                    {tpl ? templateCode(tpl) : t("workday.rota.closed_code")}
                  </span>
                </button>
              );
            })}
          </div>
          <div class="mt-4 rounded-xl bg-base-200 p-3">
            <div class="text-xs text-base-content/60">{t("workday.rota.template_for").replace("{day}", weekdayNames("long")[day.value - 1])}</div>
            <div class="flex items-center justify-between gap-2">
              <div class="font-semibold">{current?.name ?? t("workday.rota.no_template")}</div>
              {current && (
                <span class="badge badge-ghost badge-sm">{t("workday.rota.shift_count").replace("{count}", String(current.slot_count))}</span>
              )}
            </div>
          </div>
          <div class="mt-3 flex flex-wrap gap-1 rounded-xl bg-base-200 p-1">
            {props.templates.map((tpl) => (
              <button
                key={tpl.id}
                class={["btn btn-sm flex-1", props.assigned[day.value] === tpl.id ? "bg-base-100 shadow-sm" : "btn-ghost"].join(" ")}
                onClick={() => props.onAssign(day.value, tpl.id)}
              >
                {tpl.name}
              </button>
            ))}
            <button
              class={["btn btn-sm", props.assigned[day.value] == null ? "bg-base-100 shadow-sm" : "btn-ghost"].join(" ")}
              onClick={() => props.onAssign(day.value, null)}
            >
              {t("workday.rota.none")}
            </button>
          </div>
        </div>
      );
    };
  },
});
